/**
 * Financial-statement transport: the profit and loss account, the balance
 * sheet, and the indirect cash-flow statement.
 *
 * Four contract facts the screen is built on:
 *
 *   • Cumulative semantics are the trial balance's. `period` is the selected
 *     fiscal period alone; `year_to_date` sums period 1 through it. On a
 *     balance sheet the year-to-date figure IS the closing balance, because
 *     opening balances are explicit period-1 journals.
 *   • Omitting `layout_id` resolves the version in force at the period end,
 *     which is what keeps an earlier period rendering the way it did when it
 *     was current. Pinning a version is a deliberate act.
 *   • `source` says whether the figures were computed now or read from the
 *     record filed when the period closed. That is not caching: an open
 *     period's figures may still legitimately change, a closed period's must
 *     not.
 *   • Every response carries the assertions that were RUN, with both sides, so
 *     a reader can redo the subtraction. A violation is a 500 — never a 200
 *     with a caveat — and a layout that maps an account nowhere is a 422 that
 *     names every hole.
 */
import type { components } from "@erp/api-client";

import { apiFetch } from "@/lib/api/http";
import { ApiError } from "@/lib/api/errors";

type Schemas = components["schemas"];

export type FinancialStatement = Schemas["FinancialStatement"];
export type StatementLine = Schemas["StatementLine"];
export type StatementAssertion = Schemas["StatementAssertion"];
export type StatementLayoutValidation = Schemas["StatementLayoutValidation"];
export type StatementType = Schemas["StatementType"];

export interface StatementQuery {
  fiscal_period_id: string;
  layout_id?: string;
  layout_code?: string;
  currency?: string;
  comparative?: "prior_year" | "none" | "explicit";
  comparative_fiscal_period_id?: string;
  [key: string]: string | undefined;
}

const PATHS: Record<StatementType, string> = {
  profit_and_loss: "/reports/profit-and-loss",
  balance_sheet: "/reports/balance-sheet",
  cash_flow: "/reports/cash-flow",
};

export function getFinancialStatement(
  statementType: StatementType,
  query: StatementQuery,
  signal?: AbortSignal,
): Promise<FinancialStatement> {
  return apiFetch<FinancialStatement>(PATHS[statementType], { query, signal });
}

/**
 * A 422 here is not a generic failure: it carries the layout validation, so
 * the reader is told which accounts land nowhere instead of "invalid request".
 * The alternative the contract rejects is a statement that silently omits a
 * balance.
 */
export function blockedLayout(error: unknown): StatementLayoutValidation | null {
  if (!(error instanceof ApiError) || error.status !== 422) return null;
  const problem = error.problem as { validation?: StatementLayoutValidation } | undefined;
  return problem?.validation ?? null;
}
